import {
  selectDoesUserHaveRbacPermission,
  selectIsJourneyGoalsWithExitsFeatureFlagOn,
} from 'core/userContext';
import { SettingsTab } from 'modules/journey/components/SettingsTakeover/SettingsTakeover.constants';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { EXIT_RULES_MODAL } from 'modules/workflows/constants';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { closeTakeover, getOpenedNodeId, openModal, openTakeover } from 'modules/workflows/ducks';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CounterBadge } from 'ui/badge';
import { SecondaryButton } from 'ui/button';
import { NegativeFill, WarningFill } from 'ui/icons';
import { colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { TakeoverType } from '../../takeovers';

export const EXIT_CRITERIA_BUTTON = 'exit-criteria-button';
export const EXIT_CRITERIA_BADGE_COUNTER = 'exit-criteria-badge-counter';
export const EXIT_CRITERIA_ERROR = 'exit-criteria-error';
export const EXIT_CRITERIA_CLONING_ERROR = 'exit-criteria-cloning-error';

const ExitRulesButton = styled(SecondaryButton)`
  display: flex;
  flex-direction: row;
  gap: ${pxToRem(5)};
`;

export const RetryLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RetryLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
`;

export const ExitCriteriaButton = () => {
  const dispatch = useDispatch();

  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );

  const canDraftMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state as any)(PROJECT_PERMISSIONS.DraftMessaging),
  );
  const openedNodeId = useSelector(getOpenedNodeId);

  const { exitRuleCount, exitRuleCountError, hasCloningErrors, getExitRuleCriteria } =
    useExitRuleContext();

  useEffect(() => {
    if (exitRuleCountError) {
      dispatch(
        addNegativeToastWithErrorId(
          <RetryLinkContainer>
            Oops... we&apos;re having a hard time loading the Exit Rules
            <RetryLink onClick={getExitRuleCriteria} tabIndex={0}>
              Please try again.
            </RetryLink>
          </RetryLinkContainer>,
          'getExitRuleCount',
        ),
      );
    }
  }, [exitRuleCountError]);

  const handleExitRulesClick = () => {
    if (exitRuleCountError) getExitRuleCriteria();
    if (isJourneyGoalsWithExitsFeatureFlagOn) {
      dispatch(
        openTakeover(TakeoverType.Settings, {
          focusedTab: SettingsTab.ExitRules,
          canDraftMessaging,
          isOverlay: !openedNodeId,
          onClose: () => dispatch(closeTakeover(TakeoverType.Settings)),
        }),
      );
    } else {
      dispatch(openModal(EXIT_RULES_MODAL, {}));
    }
  };

  const buttonIcon = exitRuleCountError ? (
    <NegativeFill data-test={EXIT_CRITERIA_ERROR} />
  ) : (
    exitRuleCount > 0 && (
      <CounterBadge data-test={EXIT_CRITERIA_BADGE_COUNTER}> {exitRuleCount} </CounterBadge>
    )
  );

  return (
    <ExitRulesButton data-test={EXIT_CRITERIA_BUTTON} onClick={handleExitRulesClick}>
      Exit rules
      {hasCloningErrors ? (
        <WarningFill data-test={EXIT_CRITERIA_CLONING_ERROR} color={colors?.warning100} />
      ) : (
        buttonIcon
      )}
    </ExitRulesButton>
  );
};
